import type { AvatarPose } from "../../animation/AvatarController";

/**
 * Waypoints de cámara por etapa del recorrido sobre la escena única SedeScene.
 * Cada entrada define posición/target de cámara, el anillo de realce, el
 * punto al que camina el avatar en modo guiado y su pose al llegar.
 */
export type WaypointKey = "llegada" | "recepcion" | "cubiculos" | "teorica" | "preop" | "pista-moto" | "pista-auto";

export interface Waypoint {
  camera: [number, number, number];
  target: [number, number, number];
  /** Posición del anillo de realce en el suelo. */
  highlight: [number, number, number];
  highlightRadius: number;
  avatar: [number, number, number];
  /** Pose al llegar: sentado (puesto teórico) u observando (preop/pistas). */
  avatarPose?: AvatarPose;
  /** Orientación final del avatar al adoptar la pose (rad, convención atan2(x,z)). */
  avatarYaw?: number;
}

export const WAYPOINTS: Record<WaypointKey, Waypoint> = {
  llegada: {
    // 3/4 frontal desde la calle: edificio completo + entrada + avatar en primer término
    camera: [8, 9, 46],
    target: [-9, 2.5, -6],
    highlight: [-15, 0.05, -2.5],
    highlightRadius: 5,
    avatar: [-8, 0, 12],
  },
  recepcion: {
    // desde la esquina suroriental: puerta del corredor + kiosco + fila + mostrador
    camera: [-11.6, 1.9, -4.9],
    target: [-17.2, 1, -9.2],
    highlight: [-15, 0.05, -6.5],
    highlightRadius: 3.4,
    avatar: [-15.6, 0, -7.6],
  },
  cubiculos: {
    // frontal de los cubículos privados (mirando al sur)
    camera: [7.2, 1.7, -9.2],
    target: [7.6, 1.1, -6.4],
    highlight: [7.6, 0.05, -7],
    highlightRadius: 4,
    avatar: [8.8, 0, -8.6],
  },
  teorica: {
    // sobre el hombro del avatar sentado: se lee la pantalla del examen
    camera: [-15.7, 1.8, -15.3],
    target: [-14.3, 0.95, -17.7],
    highlight: [-8, 0.05, -19],
    highlightRadius: 7,
    // silla del cubículo: centrado frente al monitor, mirando al norte (al equipo)
    avatar: [-14.2, 0, -17.4],
    avatarPose: "sit",
    avatarYaw: Math.PI,
  },
  preop: {
    camera: [21, 3.4, -6],
    target: [29, 1, -15],
    highlight: [28, 0.05, -15],
    highlightRadius: 5.5,
    avatar: [25, 0, -12],
    avatarPose: "observe",
  },
  "pista-moto": {
    // 3/4 elevada: línea de salida + moto + circuito de conos
    camera: [-46, 10, 12],
    target: [-40, 0.5, -11],
    highlight: [-40, 0.05, -10],
    highlightRadius: 9,
    avatar: [-47, 0, -6],
    avatarPose: "observe",
  },
  "pista-auto": {
    // 3/4 elevada: pista + conos + vehículo en su inicio + vía pública
    camera: [32, 16, 50],
    target: [2, 0.5, 20],
    highlight: [15, 0.05, 22],
    highlightRadius: 12,
    avatar: [-12, 0, 14],
    avatarPose: "observe",
  },
};

const STAGE_TO_WAYPOINT: Record<string, WaypointKey> = {
  agendamiento: "llegada",
  llegada: "llegada",
  "recepcion-espera": "recepcion",
  "validacion-identidad": "cubiculos",
  privacidad: "recepcion",
  "sala-teorica": "teorica",
  "prueba-teorica": "teorica",
  "supervision-contingencia": "teorica",
  alistamiento: "preop",
  "destreza-moto": "pista-moto",
  "destreza-auto": "pista-auto",
  "via-publica": "pista-auto",
  cierre: "recepcion",
};

export function waypointKeyForStage(stageId: string): WaypointKey {
  return STAGE_TO_WAYPOINT[stageId] ?? "llegada";
}
